import React, { useState } from 'react';
import { useCollaboratorByIdQuery } from '@/features/Collaborators/Data';
import type { Movimentacao, MetodoEntrega, TipoSolicitacao } from '../../../models';
import { useMovimentacaoMutations } from '../../../hooks/data';
import { METODO_ENTREGA_LABELS } from '../solicitacaoDetailsPage.definitions';
import { dataInputParaDate } from '../../../utils/dataInput';
import { pareceSerAtendimentoPresencial } from '../../../utils/mensagensContato';
import ContatoColaboradorBotoes from './ContatoColaboradorBotoes';

interface MovimentacaoLogisticaSectionProps {
  solicitacaoId: string;
  movimentacao: Movimentacao;
  /** SI — edita método de entrega/código de rastreio e as duas datas livremente. */
  podeEditar: boolean;
  /** Colaborador-alvo — edita só a data que cabe a ele registrar (ver `tipoFluxo`
   * abaixo), nunca método de entrega/código de rastreio. */
  isColaboradorAlvo: boolean;
  colaboradorId: string | null;
  /** Admissão/Desligamento: só WhatsApp (decisão registrada — nem sempre a pessoa já tem
   * Teams corporativo nessas duas pontas do processo). Demais tipos: WhatsApp + Teams. */
  tipoSolicitacao: TipoSolicitacao;
  /** Admissão: telefone/endereço do Pré-cadastro (a pessoa ainda pode nem estar vinculada). */
  preCadastroId: string | null;
  nomeColaboradorPendente: string | null;
}

/** Uber/pessoalmente: entrega e recebimento acontecem na mesma hora — 1 campo só, que
 * grava as duas datas juntas, em vez de pedir a mesma informação duas vezes. */
const METODOS_COM_DATA_UNICA: MetodoEntrega[] = ['uber', 'pessoalmente'];

function paraInputDate(timestamp: Movimentacao['dataEnvio']): string {
  if (!timestamp) return '';
  const data = 'toDate' in timestamp ? timestamp.toDate() : new Date(timestamp as unknown as string);
  return data.toISOString().slice(0, 10);
}

function formatarDataCurta(timestamp: Movimentacao['dataEnvio']): string | null {
  if (!timestamp) return null;
  const data = 'toDate' in timestamp ? timestamp.toDate() : new Date(timestamp as unknown as string);
  return data.toLocaleDateString('pt-BR');
}

/**
 * Método de entrega, código de rastreio e datas de postagem/recebimento — formulário direto
 * na gaveta da Movimentação (um "Salvar" só), com edição contínua pro SI. As
 * datas de postagem (`dataEnvio`) e recebimento (`dataRecebimento`) também podem ser
 * registradas por quem executa a ação física, sem precisar do SI:
 * - Devolução: quem posta é o colaborador-alvo (ele sabe quando postou).
 * - Envio: quem recebe é o colaborador-alvo (ele sabe quando recebeu).
 * Ver firestore.rules — só esses 2 campos ficam abertos ao colaborador-alvo via `update`.
 */
export default function MovimentacaoLogisticaSection({
  solicitacaoId,
  movimentacao,
  podeEditar,
  isColaboradorAlvo,
  colaboradorId,
  tipoSolicitacao,
  preCadastroId,
  nomeColaboradorPendente,
}: MovimentacaoLogisticaSectionProps) {
  const { atualizarMovimentacao } = useMovimentacaoMutations(solicitacaoId);
  const { data: colaborador } = useCollaboratorByIdQuery(colaboradorId ?? undefined);

  // Formulário direto (gaveta): tudo editável de uma vez e um "Salvar" só. A gaveta remonta
  // ao trocar de Movimentação, então o estado inicial vem sempre do que está gravado.
  const [metodoEntregaInput, setMetodoEntregaInput] = useState<MetodoEntrega | ''>(movimentacao.metodoEntrega ?? '');
  const [codigoRastreioInput, setCodigoRastreioInput] = useState(movimentacao.codigoRastreio ?? '');
  const [dataEnvioInput, setDataEnvioInput] = useState(paraInputDate(movimentacao.dataEnvio));
  const [dataRecebimentoInput, setDataRecebimentoInput] = useState(paraInputDate(movimentacao.dataRecebimento));

  const usaDataUnica = metodoEntregaInput ? METODOS_COM_DATA_UNICA.includes(metodoEntregaInput) : false;

  const podeColaboradorEditarEnvio = isColaboradorAlvo && movimentacao.tipoFluxo === 'devolucao';
  const podeColaboradorEditarRecebimento = isColaboradorAlvo && movimentacao.tipoFluxo === 'envio';
  // Com data única, quem tiver qualquer uma das 2 permissões acima registra pelos dois.
  const podeEditarEnvio = podeEditar || podeColaboradorEditarEnvio || (usaDataUnica && podeColaboradorEditarRecebimento);
  const podeEditarRecebimento = podeEditar || podeColaboradorEditarRecebimento;
  const podeSalvarAlgo = podeEditar || podeColaboradorEditarEnvio || podeColaboradorEditarRecebimento;

  const alterado =
    metodoEntregaInput !== (movimentacao.metodoEntrega ?? '') ||
    codigoRastreioInput !== (movimentacao.codigoRastreio ?? '') ||
    dataEnvioInput !== paraInputDate(movimentacao.dataEnvio) ||
    (!usaDataUnica && dataRecebimentoInput !== paraInputDate(movimentacao.dataRecebimento));

  // Admissão/Desligamento: só WhatsApp (decisão registrada). Demais tipos: WhatsApp + Teams.
  const mostrarTeams = tipoSolicitacao !== 'admissao' && tipoSolicitacao !== 'desligamento';
  // Só relevante em desligamento sem código — pula o pedido de endereço quando a cidade
  // cadastrada no perfil (endereço principal) é BH/região (ver decisão registrada: direto
  // do cadastro, sem confirmação manual).
  const enderecoPrincipal = colaborador?.addresses?.find((endereco) => endereco.type === 'main');
  const atendimentoPresencial = pareceSerAtendimentoPresencial(enderecoPrincipal?.city);
  const handleSalvar = () => {
    // Campo de data vazio (o "x" do input nativo) salva null — dá pra desmarcar.
    const paraData = (valor: string) => (valor ? (dataInputParaDate(valor) as any) : null);
    const dataEnvio = paraData(dataEnvioInput);
    // Uber/pessoalmente: entrega e recebimento na mesma hora — grava as duas iguais.
    const dataRecebimento = usaDataUnica ? dataEnvio : paraData(dataRecebimentoInput);
    // O colaborador-alvo só alcança a data dele (firestore.rules) — não manda o resto.
    atualizarMovimentacao.mutate({
      id: movimentacao.id,
      ...(podeEditar && metodoEntregaInput
        ? {
            metodoEntrega: metodoEntregaInput,
            codigoRastreio: metodoEntregaInput === 'correios' ? codigoRastreioInput.trim() || null : null,
          }
        : {}),
      ...(podeEditarEnvio ? { dataEnvio } : {}),
      ...(podeEditarRecebimento || (usaDataUnica && podeEditarEnvio) ? { dataRecebimento } : {}),
    });
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm text-gray-700">
        Método de entrega
        <select
          className="input-gaveta mt-1"
          value={metodoEntregaInput}
          disabled={!podeEditar}
          onChange={(event) => setMetodoEntregaInput(event.target.value as MetodoEntrega)}
        >
          <option value="">A definir</option>
          {Object.entries(METODO_ENTREGA_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      {metodoEntregaInput === 'correios' && (
        <label className="block text-sm text-gray-700">
          Código do objeto
          <input
            type="text"
            className="input-gaveta mt-1"
            placeholder="Ex.: QR123456789BR"
            value={codigoRastreioInput}
            disabled={!podeEditar}
            onChange={(event) => setCodigoRastreioInput(event.target.value.toUpperCase())}
          />
        </label>
      )}

      {usaDataUnica ? (
        <label className="block text-sm text-gray-700">
          Data de entrega
          <input
            type="date"
            className="input-gaveta mt-1"
            value={dataEnvioInput}
            disabled={!podeEditarEnvio}
            onChange={(event) => setDataEnvioInput(event.target.value)}
          />
        </label>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm text-gray-700">
            Data de postagem
            <input
              type="date"
              className="input-gaveta mt-1"
              value={dataEnvioInput}
              disabled={!podeEditarEnvio}
              onChange={(event) => setDataEnvioInput(event.target.value)}
            />
          </label>
          <label className="block text-sm text-gray-700">
            Data de recebimento
            <input
              type="date"
              className="input-gaveta mt-1"
              value={dataRecebimentoInput}
              disabled={!podeEditarRecebimento}
              onChange={(event) => setDataRecebimentoInput(event.target.value)}
            />
          </label>
        </div>
      )}

      {podeSalvarAlgo && (
        <button
          type="button"
          className="btn-secondary text-sm"
          disabled={!alterado || atualizarMovimentacao.isPending}
          onClick={handleSalvar}
        >
          {atualizarMovimentacao.isPending ? 'Salvando...' : 'Salvar'}
        </button>
      )}

      {podeEditar && (colaborador || nomeColaboradorPendente) && (
        <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Contato com a pessoa</p>
        <ContatoColaboradorBotoes
          situacao={{
            tipoSolicitacao,
            tipoFluxo: movimentacao.tipoFluxo,
            codigoRastreio: movimentacao.codigoRastreio ?? null,
            atendimentoPresencial,
          }}
          colaborador={colaborador ?? null}
          nomeColaboradorPendente={nomeColaboradorPendente}
          preCadastroId={tipoSolicitacao === 'admissao' ? preCadastroId : null}
          mostrarTeams={mostrarTeams}
          dataPrevista={formatarDataCurta(movimentacao.dataPrevista)}
        />
        </div>
      )}
    </div>
  );
}
